/**
 * Hotel Reliability Rewards Settings Panel
 * Allows hotel owners to configure reliability rewards
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { reliability } from '../../lib/api';
import { Settings, Save, Info, Trophy, Star, Award } from 'lucide-react';
import { toast } from 'sonner';

export const ReliabilitySettingsPanel = ({ hotelId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [discounts, setDiscounts] = useState({
    Excellent: 10,
    Good: 5,
    Standard: 0
  });

  useEffect(() => {
    fetchSettings();
  }, [hotelId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await reliability.getHotelSettings(hotelId);
      if (response.data.success) {
        setEnabled(response.data.enabled);
        setDiscounts(response.data.discounts);
      }
    } catch (error) {
      console.error('Error fetching reliability settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validate discounts
      if (discounts.Excellent < 0 || discounts.Excellent > 50) {
        toast.error('Excellent tier discount must be between 0-50%');
        return;
      }
      if (discounts.Good < 0 || discounts.Good > 50) {
        toast.error('Good tier discount must be between 0-50%');
        return;
      }
      
      await reliability.updateHotelSettings(hotelId, {
        enabled: enabled,
        discounts: discounts
      });
      
      toast.success('✅ Reliability Rewards settings saved!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Reliability Rewards Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="reliability-settings-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            <CardTitle>Reliability Rewards Settings</CardTitle>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="reliability-enabled-toggle"
          />
        </div>
        <CardDescription>
          Configure discount tiers for reliable guests
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription className="text-xs">
            Reliability Rewards incentivizes guests with low cancellation rates by offering automatic discounts. Guests see their tier and savings in real-time.
          </AlertDescription>
        </Alert>

        {enabled ? (
          <>
            {/* Excellent Tier */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-600" />
                <Label htmlFor="excellent-discount" className="font-semibold">Excellent Tier Discount</Label>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="excellent-discount"
                  type="number"
                  min="0"
                  max="50"
                  value={discounts.Excellent}
                  onChange={(e) => setDiscounts({...discounts, Excellent: parseInt(e.target.value) || 0})}
                  data-testid="excellent-discount-input"
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">% discount</span>
              </div>
              <p className="text-xs text-muted-foreground">
                For guests with score ≥80, cancellation rate &lt;10%, zero no-shows
              </p>
            </div>

            <Separator />

            {/* Good Tier */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-emerald-600" />
                <Label htmlFor="good-discount" className="font-semibold">Good Tier Discount</Label>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="good-discount"
                  type="number"
                  min="0"
                  max="50"
                  value={discounts.Good}
                  onChange={(e) => setDiscounts({...discounts, Good: parseInt(e.target.value) || 0})}
                  data-testid="good-discount-input"
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">% discount</span>
              </div>
              <p className="text-xs text-muted-foreground">
                For guests with score ≥60, cancellation rate &lt;30%
              </p>
            </div>

            <Separator />

            {/* Standard Tier */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-slate-600" />
                <Label className="font-semibold">Standard Tier</Label>
              </div>
              <p className="text-sm text-muted-foreground">0% discount (new guests building history)</p>
            </div>

            <Separator />

            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full"
              data-testid="save-reliability-settings-btn"
            >
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </>
        ) : (
          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              Reliability Rewards are currently <strong>disabled</strong>. All guests will see standard pricing. Enable above to activate rewards.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
